/**
 * @module public/js/pages/loginAuthConfig
 * @summary Loads public pre-login auth-provider configuration.
 */

import { getDoc } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import useTenant from '../core/useTenant.js';
import { docWithTenant } from '../utils/firestoreHelpers.js';

export const DEFAULT_LOGIN_TENANT_ID = 'default';

/**
 * Resolve the tenant whose public login auth settings should drive buttons.
 *
 * @param {object} [options] Resolution options.
 * @param {Window} [options.window] Browser window, injected for tests.
 * @param {string} [options.locationSearch] Query string override.
 * @param {() => { getTenantId?: () => string | null }} [options.useTenantImpl] Tenant hook.
 * @returns {string} Tenant id for pre-login auth controls.
 */
export function resolveLoginTenantId({
  window: providedWindow,
  locationSearch,
  useTenantImpl = useTenant,
} = {}) {
  const win = providedWindow ?? (typeof window !== 'undefined' ? window : null);
  const search =
    typeof locationSearch === 'string'
      ? locationSearch
      : win?.location?.search || '';
  const params = new URLSearchParams(search);
  const queryTenant = String(
    params.get('tenantId') || params.get('tenant') || ''
  ).trim();
  if (queryTenant) {
    return queryTenant;
  }

  return useTenantImpl?.()?.getTenantId?.() || DEFAULT_LOGIN_TENANT_ID;
}

/**
 * Identify optional public-login config reads that should fall back silently.
 *
 * @param {unknown} error Firestore read error.
 * @returns {boolean} True when email/password defaults should be used.
 */
export function isOptionalLoginAuthConfigReadError(error) {
  const err =
    /** @type {{ code?: string, status?: string, message?: string }} */ (
      error || {}
    );
  const code = String(err.code || err.status || '').toLowerCase();
  if (
    code === 'permission-denied' ||
    code === 'firestore/permission-denied' ||
    code === 'permission_denied' ||
    code === 'not-found' ||
    code === 'not_found'
  ) {
    return true;
  }

  const message = String(err.message || error || '').toLowerCase();
  return (
    message.includes('missing or insufficient permissions') ||
    message.includes('permission-denied') ||
    message.includes('permission_denied')
  );
}

/**
 * Load non-secret tenant auth settings for the pre-login page.
 *
 * Missing or denied optional auth config resolves to an empty config so the
 * login page keeps the email/password form available without noisy warnings.
 *
 * @param {object} [options] Loader dependencies and overrides.
 * @param {string} [options.tenantId] Tenant id override.
 * @param {Window} [options.window] Browser window, injected for tests.
 * @param {string} [options.locationSearch] Query string override.
 * @param {() => { getTenantId?: () => string | null }} [options.useTenantImpl] Tenant hook.
 * @param {(tenantId: string, path: string) => unknown} [options.docFactory] Firestore ref factory.
 * @param {(ref: unknown) => Promise<{ exists?: boolean | (() => boolean), data?: () => Record<string, unknown> | null }>} [options.fetchDoc] Firestore getter.
 * @returns {Promise<Record<string, unknown>>} Stored auth settings, if present.
 */
export async function loadLoginAuthConfig({
  tenantId,
  window: providedWindow,
  locationSearch,
  useTenantImpl = useTenant,
  docFactory = docWithTenant,
  fetchDoc = getDoc,
} = {}) {
  const resolvedTenantId =
    tenantId ||
    resolveLoginTenantId({
      window: providedWindow,
      locationSearch,
      useTenantImpl,
    });

  try {
    const snap = await fetchDoc(docFactory(resolvedTenantId, 'AppConfig/auth'));
    const exists =
      typeof snap?.exists === 'function'
        ? snap.exists()
        : Boolean(snap?.exists);
    if (!exists) {
      return {};
    }

    const data = snap.data?.() || {};
    return data && typeof data === 'object' ? data : {};
  } catch (error) {
    if (isOptionalLoginAuthConfigReadError(error)) {
      return {};
    }
    throw error;
  }
}
